import React, { useState } from "react"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Button from "../../../components/ui/Button"
import { Card, CardContent } from "../../../components/ui/card"
import { postAnnouncement, removeAnnouncement } from "../../../utils/cohortHelpers"

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "")

const CohortAnnouncementsTab = ({ orgId, cohortId, announcements, onChange }) => {
  const [showForm, setShowForm] = useState(false)
  const [title, setTitle] = useState("")
  const [body, setBody] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const handlePost = async () => {
    if (!title?.trim() || !body?.trim()) {
      setError("Title and message are both required")
      return
    }
    setSaving(true)
    const { error: saveError } = await postAnnouncement(orgId, cohortId, { title, body })
    setSaving(false)
    if (saveError) {
      setError(saveError?.message || "Could not post announcement")
      return
    }
    setTitle("")
    setBody("")
    setShowForm(false)
    onChange?.()
  }

  const handleDelete = async (announcementId) => {
    if (!window.confirm("Delete this announcement?")) return
    await removeAnnouncement(orgId, cohortId, announcementId)
    onChange?.()
  }

  const sorted = [...(announcements || [])].sort((a, b) => new Date(b?.postedAt || 0) - new Date(a?.postedAt || 0))

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{announcements?.length || 0} announcement{announcements?.length === 1 ? "" : "s"}</p>
        <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => { setShowForm(true); setError("") }}>
          New Announcement
        </Button>
      </div>

      {showForm && (
        <Card className="dark:bg-transparent">
          <CardContent className="pt-5 space-y-4">
            <Input label="Title" placeholder="e.g. Cohort kickoff moved" value={title} onChange={(e) => setTitle(e?.target?.value)} />
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Message</label>
              <textarea
                rows={4}
                value={body}
                onChange={(e) => setBody(e?.target?.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-3">
              <Button variant="outline" fullWidth onClick={() => setShowForm(false)}>Cancel</Button>
              <Button variant="default" fullWidth loading={saving} onClick={handlePost}>Post Announcement</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {sorted?.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Icon name="Megaphone" size={24} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No announcements posted yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sorted?.map((a) => (
            <div key={a?.id} className="bg-card border border-border rounded-xl p-4 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{a?.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{formatDate(a?.postedAt)}</p>
                <p className="text-sm text-foreground mt-2 whitespace-pre-wrap">{a?.body}</p>
              </div>
              <Button variant="ghost" size="icon" iconName="Trash2" iconSize={14} onClick={() => handleDelete(a?.id)} className="shrink-0" />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default CohortAnnouncementsTab